import { Button, Card, CardBody, CardHeader, Chip, Code } from "@heroui/react";
import { useRun } from "../lib/state.tsx";
import { fmtBytes, fmtNumber } from "../lib/api.ts";
import { Json } from "../components/Json.tsx";
import { Section, Stat } from "../components/Section.tsx";

interface CodeExecResult {
  toolLoop: {
    totals: { schemaTokens: number; messageTokens: number; total: number; source: string };
    contextBytes: number;
    answer: string;
    toolCalls: Array<{ name: string; resultBytes: number }>;
  };
  sandbox: {
    ok: boolean;
    error?: string;
    value?: Record<string, unknown>;
    toolCalls: number;
    toolBytes: number;
    durationMs: number;
    timedOut: boolean;
    logs: string[];
  };
  comparison: {
    toolLoopTokens: number;
    scriptTokens: number;
    ratio: number | null;
    source: string;
    bytesKeptOutOfContext: number;
  };
}

export function CodeExecPanel() {
  const run = useRun<CodeExecResult>();
  const d = run.data;

  return (
    <div className="flex flex-col gap-4">
      <Section
        title="One task, two paths"
        blurb="Filter the 2025-Q2 expense claims above the ADR-006 threshold and write a summary note. Path A hands every row to the model. Path B hands the model a sandbox and lets it write ten lines of JavaScript. Both produce the same numbers; only one of them pays for the rows."
      >
        <Button color="primary" isLoading={run.pending} onPress={() => void run.run("codeexec", { paceMs: 60 })}>
          Run both
        </Button>
        {run.error && <p className="mt-2 text-xs text-danger">{run.error}</p>}

        {d && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Tool loop"
              value={fmtNumber(d.comparison.toolLoopTokens)}
              sub="tokens through context"
              tone="danger"
            />
            <Stat
              label="Generated script"
              value={fmtNumber(d.comparison.scriptTokens)}
              sub="tokens through context"
              tone="success"
            />
            <Stat
              label="Ratio"
              value={d.comparison.ratio ? `${d.comparison.ratio}×` : "—"}
              sub="cheaper"
              tone="primary"
            />
            <Stat
              label="Never reached context"
              value={fmtBytes(d.comparison.bytesKeptOutOfContext)}
              sub="fetched and discarded inside the sandbox"
              tone="warning"
            />
          </div>
        )}
      </Section>

      {d && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card shadow="none" className="border border-danger/40 bg-danger/5">
            <CardHeader className="flex flex-col items-start gap-1 pb-1">
              <span className="text-sm font-medium">Path A — the tool loop</span>
              <span className="text-xs text-default-400">
                Every row of the result is a message in the transcript, and the transcript is
                re-sent on every subsequent turn.
              </span>
            </CardHeader>
            <CardBody className="pt-0">
              <div className="mb-2 flex flex-wrap gap-2">
                {d.toolLoop.toolCalls.map((c, i) => (
                  <Chip key={i} size="sm" variant="flat" className="font-mono text-[10px]">
                    {c.name} → {fmtBytes(c.resultBytes)}
                  </Chip>
                ))}
              </div>
              <p className="font-mono text-xs text-danger">
                {fmtBytes(d.toolLoop.contextBytes)} of tool output entered the context window
              </p>
              <p className="mt-2 text-xs text-default-300">{d.toolLoop.answer}</p>
            </CardBody>
          </Card>

          <Card shadow="none" className="border border-success/40 bg-success/5">
            <CardHeader className="flex flex-col items-start gap-1 pb-1">
              <span className="text-sm font-medium">Path B — a script in the sandbox</span>
              <span className="text-xs text-default-400">
                The rows are fetched, filtered and aggregated inside a child process. Only the
                summary object comes back.
              </span>
            </CardHeader>
            <CardBody className="pt-0">
              <div className="mb-2 flex flex-wrap gap-2">
                <Chip size="sm" variant="flat" color={d.sandbox.ok ? "success" : "danger"}>
                  {d.sandbox.ok ? "completed" : d.sandbox.timedOut ? "timed out" : "failed"}
                </Chip>
                <Chip size="sm" variant="flat" className="font-mono text-[10px]">
                  {d.sandbox.toolCalls} mcp calls · {fmtBytes(d.sandbox.toolBytes)}
                </Chip>
                <Chip size="sm" variant="flat" className="font-mono text-[10px]">
                  {Math.round(d.sandbox.durationMs)} ms
                </Chip>
              </div>
              {d.sandbox.logs.map((l, i) => (
                <Code key={i} size="sm" className="mb-1 block w-full text-[11px]">
                  {l}
                </Code>
              ))}
              {d.sandbox.error && <p className="text-xs text-danger">{d.sandbox.error}</p>}
              <Json value={d.sandbox.value} className="mt-2 max-h-64 overflow-y-auto" />
            </CardBody>
          </Card>
        </div>
      )}

      <Section
        title="Be honest about the sandbox"
        blurb="This is demo-grade isolation, not a production sandbox."
        tone="danger"
      >
        <ul className="list-inside list-disc space-y-1 text-xs text-default-300">
          <li>A child process with no environment variables and an empty working directory.</li>
          <li>A two-second wall clock and a 256 KB output cap, both enforced by the parent.</li>
          <li>
            <code className="text-warning">fetch</code> is replaced by a shim that enforces gate 4 —
            the runner may reach this server's <code className="text-warning">/mcp</code> endpoint
            and nothing else, not even a neighbouring path on the same origin.
          </li>
          <li>
            A determined escape from a same-host child process is <em>not</em> prevented. Never
            point this app at real credentials or a real network. Every secret in it is fake by
            construction.
          </li>
        </ul>
      </Section>
    </div>
  );
}
